/**
 * Exacta Overlays (user's rule of thumb, no takeout):
 *   Fair $1 payoff for A over B = Win(A) × (Win(B) + 1); $2 = 2 × that.
 *   Actual = Cybertote Exacta probables ÷ publishedBase × base. Cybertote
 *   quotes probables at the track's exacta minimum ($1 most tracks, $2 NYRA /
 *   Finger Lakes); the backend detects it from today's results.
 *   Overlay % = (Actual − Fair) ÷ Fair × 100.
 * Win odds come from the same probables page so both sides share one race.
 */

export const OVERLAY_THRESHOLDS = [10, 20, 30, 40];
export const EXACTA_DEFAULT_BASE = 1;

// "5-2", "5/2", "3" → 2.5 / 2.5 / 3. "SC", "--" → null.
export const parseFractional = (s) => {
  if (s == null) return null;
  const str = String(s).trim();
  const m = str.match(/^(\d+(?:\.\d+)?)\s*[/-]\s*(\d+(?:\.\d+)?)$/);
  if (m) {
    const d = parseFloat(m[2]);
    return d ? parseFloat(m[1]) / d : null;
  }
  if (!/^\d+(?:\.\d+)?$/.test(str)) return null;
  return parseFloat(str);
};

const parseMoney = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const fairExacta = (winA, winB, base = EXACTA_DEFAULT_BASE) =>
  winA > 0 && winB >= 0 ? base * winA * (winB + 1) : null;

export const buildOverlayMatrix = ({
  leftRows,
  odds,
  exactaMatrix,
  thresholdPct,
  base = EXACTA_DEFAULT_BASE,
  publishedBase = 1,
}) => {
  const ranked = (leftRows || [])
    .filter((r) => r.RANK && r.S != null)
    .slice()
    .sort((a, b) => (a.RANK || 0) - (b.RANK || 0))
    .map((r) => Number(r.S));

  const winOf = (post) => parseFractional(odds?.[post] ?? odds?.[String(post)]);

  const getActual = (first, second) => {
    const row = exactaMatrix?.[first] || exactaMatrix?.[String(first)];
    const n = parseMoney(row?.[second] ?? row?.[String(second)]);
    return n == null ? null : (n / publishedBase) * base;
  };

  const n = ranked.length;
  const cells = [];
  let rankCounter = 0;
  // Walk anti-diagonals of the DDPO-rank grid; number only overlays.
  for (let d = 1; d < 2 * n - 1; d += 1) {
    for (let i = 0; i < n; i += 1) {
      const j = d - i;
      if (j < 0 || j >= n || i === j) continue;
      const first = ranked[i];
      const second = ranked[j];
      const actual = getActual(first, second);
      const wa = winOf(first);
      const wb = winOf(second);
      const implied = wa != null && wb != null ? fairExacta(wa, wb, base) : null;
      const overlayPct =
        actual != null && implied ? ((actual - implied) / implied) * 100 : null;
      const isOverlay = overlayPct != null && overlayPct >= thresholdPct;
      cells.push({
        firstPost: first,
        secondPost: second,
        rowRank: j + 1,
        colRank: i + 1,
        actual,
        implied,
        overlayPct,
        isOverlay,
        rank: isOverlay ? ++rankCounter : null,
      });
    }
  }
  return { ranked, cells };
};

// First `n` combos: numbered overlays by rank, then priced non-overlays in grid
// order. Grouped by 1st-place horse in order of first appearance.
export const buildOverlayTicket = (cells, n) => {
  const priced = cells.filter((c) => c.actual != null);
  const picks = [
    ...priced.filter((c) => c.isOverlay).sort((a, b) => a.rank - b.rank),
    ...priced.filter((c) => !c.isOverlay),
  ].slice(0, Math.max(0, n));
  const groups = [];
  for (const c of picks) {
    let g = groups.find((x) => x.first === c.firstPost);
    if (!g) groups.push((g = { first: c.firstPost, seconds: [] }));
    g.seconds.push(c.secondPost);
  }
  return { groups, combos: picks.length, overlays: picks.filter((c) => c.isOverlay).length };
};
